/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getElementData } from '#utils/UNSAFE_visUtils';
import type { RefObject } from 'preact';
import { useRef } from 'preact/hooks';
import { Scale } from '#utils/UNSAFE_visTypes/chart';
import { getAxisBoundsByDelta, getAxisMinMaxDrag, panBy } from './utils';
import { usePinch } from '#PRIVATE_TimeComponent/hooks/usePinch';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { AxisInfo } from '#PRIVATE_Axis/axis.types';

type ViewportHandlerProps = {
  rootRef: RefObject<HTMLDivElement>;
  width: number;
  height: number;
  plotAreaSpace: { x: number; y: number; width: number; height: number };
  isRtl: boolean;
  isHoriz: boolean;
  xAxisInfo: AxisInfo;
  yAxisInfo: AxisInfo;
  y2AxisInfo: AxisInfo;
  yScale: Scale;
  y2Scale: Scale;
  xScale: Scale;
  zoomAndScroll: 'live' | 'off';
  onViewportChange?: (detail: any) => void;
};

const VIEWPORT_SCALE = 0.05;
const PAN_RATIO = 0.25;

export function useChartViewport({
  plotAreaSpace,
  yScale,
  y2Scale,
  xScale,
  onViewportChange,
  isHoriz,
  zoomAndScroll,
  isRtl,
  rootRef,
  xAxisInfo,
  yAxisInfo,
  y2AxisInfo
}: ViewportHandlerProps) {
  const isPointerClicked = useRef(false);
  const handleViewportChange = (delta: number, origin: { x: number; y: number }) => {
    const deltaXMin = delta * (origin.x - plotAreaSpace.x);
    const deltaXMax = -delta * (plotAreaSpace.x + plotAreaSpace.width - origin.x);
    const deltaYMin = delta * (origin.y - plotAreaSpace.y);
    const deltaYMax = -delta * (plotAreaSpace.y + plotAreaSpace.height - origin.y);
    const deltaY2Min = delta * (origin.y - plotAreaSpace.y);
    const deltaY2Max = -delta * (plotAreaSpace.y + plotAreaSpace.height - origin.y);
    const extents = getAxisBoundsByDelta(
      xAxisInfo,
      xAxisInfo,
      yAxisInfo,
      y2AxisInfo,
      yAxisInfo,
      y2AxisInfo,
      xScale,
      yScale,
      y2Scale,
      deltaXMin,
      deltaXMax,
      deltaYMin,
      deltaYMax,
      deltaY2Min,
      deltaY2Max,
      isRtl,
      isHoriz
    );
    onViewportChange?.({
      xMin: extents.xExtents.viewportMin,
      yMin: extents.yExtents.viewportMin,
      xMax: extents.xExtents.viewportMax,
      yMax: extents.yExtents.viewportMax,
      y2Min: extents.y2Extents.viewportMax,
      y2Max: extents.y2Extents.viewportMax
    });
  };

  const pinchProps = usePinch({
    captureTargetRef: rootRef,
    onPinchZoomChange: ({ delta, origin }) => {
      const d = (isHoriz ? 1 : -1) * -1 * delta * VIEWPORT_SCALE;
      handleViewportChange(d, origin);
    }
  });

  pinchProps.onPointerOut = undefined;

  const onWheel = (event: WheelEvent) => {
    const delta = (isHoriz ? 1 : -1) * event.deltaY * VIEWPORT_SCALE;
    handleViewportChange(delta, { x: event.offsetX, y: event.offsetY });
    event.preventDefault();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    const zoomIn = key === '+' || key === '=';
    const zoomOut = key === '-' || key === '_';

    const isPageUp = key === 'PageUp';
    const isPageDown = key === 'PageDown';

    if (!zoomIn && !zoomOut && !isPageUp && !isPageDown) {
      return;
    }
    const zoomRatio = zoomIn ? 1.5 : 1 / 1.5;
    const zoomShiftRatio = (1 / zoomRatio - 1) / 2;

    let deltaXMin = 0,
      deltaXMax = 0,
      deltaYMin = 0,
      deltaYMax = 0,
      deltas;

    if (zoomIn || zoomOut) {
      deltaXMin = -1 * zoomShiftRatio * plotAreaSpace.width;
      deltaXMax = zoomShiftRatio * plotAreaSpace.width;
      deltaYMin = -1 * zoomShiftRatio * plotAreaSpace.height;
      deltaYMax = zoomShiftRatio * plotAreaSpace.height;
    } else {
      deltas = panBy(plotAreaSpace, isPageUp, PAN_RATIO, isHoriz, isRtl);
      if (deltas) {
        deltaXMin = deltas.deltaX;
        deltaXMax = deltas.deltaX;
        deltaYMin = deltas.deltaY;
        deltaYMax = deltas.deltaY;
      }
    }

    const extents = getAxisBoundsByDelta(
      xAxisInfo,
      xAxisInfo,
      yAxisInfo,
      y2AxisInfo,
      yAxisInfo,
      y2AxisInfo,
      xScale,
      yScale,
      y2Scale,
      deltaXMin,
      deltaXMax,
      deltaYMin,
      deltaYMax,
      deltaYMin,
      deltaYMax,
      isRtl,
      isHoriz
    );
    onViewportChange?.({
      xMin: extents.xExtents.viewportMin,
      yMin: extents.yExtents.viewportMin,
      xMax: extents.xExtents.viewportMax,
      yMax: extents.yExtents.viewportMax,
      y2Min: extents.y2Extents.viewportMax,
      y2Max: extents.y2Extents.viewportMax
    });
  };

  const onPointerDown = (event: PointerEvent) => {
    isPointerClicked.current = true;
    const obj = getElementData(rootRef, event.target as HTMLElement);
    if (obj && obj['ojPrivateObject'] === 'scrollBar') {
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
      const { xMin, xMax } = getAxisMinMaxDrag(
        event.offsetX,
        event.offsetY,
        plotAreaSpace,
        xAxisInfo,
        xAxisInfo,
        isHoriz,
        isRtl
      );

      onViewportChange?.({
        xMin,
        yMin: yAxisInfo.viewportMin,
        xMax,
        yMax: yAxisInfo.viewportMax
      });
    }
  };

  const onPointerUp = () => {
    isPointerClicked.current = false;
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!isPointerClicked.current) {
      return;
    }

    const obj = getElementData(rootRef, event.target as HTMLElement);
    if (obj && obj['ojPrivateObject'] === 'scrollBar') {
      const { xMin, xMax } = getAxisMinMaxDrag(
        event.offsetX,
        event.offsetY,
        plotAreaSpace,
        xAxisInfo,
        xAxisInfo,
        isHoriz,
        isRtl
      );

      onViewportChange?.({
        xMin,
        yMin: yAxisInfo.viewportMin,
        xMax,
        yMax: yAxisInfo.viewportMax
      });
    }
  };
  return onViewportChange && zoomAndScroll === 'live'
    ? mergeProps({ onWheel, onKeyDown, onPointerMove, onPointerDown, onPointerUp }, pinchProps)
    : {};
}
